import {
  createSlice,
  createAsyncThunk,
} from "@reduxjs/toolkit";

import api from "../../services/AxiosInstance";

// ============================================================
// WATER DCB TABLE DATA
//
// GET /api/reports/water-dcb
// ============================================================

export const fetchWaterDcbThunk = createAsyncThunk(
  "waterDcb/fetch",

  async (filters = {}, { rejectWithValue }) => {
    try {
      const params = {
        fy_year: filters?.fy_year || "",
        from_date: filters?.from_date || "",
        to_date: filters?.to_date || "",
        ward_id: filters?.ward_id || "",
        agent_id: filters?.agent_id || "",

        ...(filters?.home_id
          ? {
              home_id: filters.home_id,
            }
          : {}),

        page: Number(filters?.page || 1),

        pageSize: Number(
          filters?.pageSize || 20,
        ),

        sort_field:
          filters?.sort_field || "home_id",

        sort_direction:
          String(
            filters?.sort_direction || "desc",
          ).toLowerCase() === "asc"
            ? "asc"
            : "desc",
      };

      const response = await api.get(
        "/api/reports/water-dcb",
        {
          params,
        },
      );

      if (!response.data?.success) {
        return rejectWithValue(
          response.data?.message ||
            "Failed to load Water DCB report",
        );
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to load Water DCB report",
      );
    }
  },
);

// ============================================================
// COMMON EXPORT ERROR HELPER
// ============================================================

const getBlobErrorMessage = async (
  error,
  defaultMessage,
) => {
  try {
    if (
      error?.response?.data instanceof Blob
    ) {
      const text =
        await error.response.data.text();

      const parsed =
        JSON.parse(text);

      return (
        parsed?.message ||
        defaultMessage
      );
    }

    return (
      error?.response?.data?.message ||
      error?.message ||
      defaultMessage
    );
  } catch (_) {
    return defaultMessage;
  }
};

// ============================================================
// WATER DCB EXCEL EXPORT
//
// GET /api/reports/water-dcb/export?format=excel
// ============================================================

export const exportWaterDcbExcelThunk =
  createAsyncThunk(
    "waterDcb/exportExcel",

    async (
      filters = {},
      { rejectWithValue },
    ) => {
      try {
        const params = {
          format: "excel",

          fy_year:
            filters?.fy_year || "",

          from_date:
            filters?.from_date || "",

          to_date:
            filters?.to_date || "",

          ward_id:
            filters?.ward_id || "",

          agent_id:
            filters?.agent_id || "",

          ...(filters?.home_id
            ? {
                home_id:
                  filters.home_id,
              }
            : {}),

          sort_field:
            filters?.sort_field ||
            "home_id",

          sort_direction:
            String(
              filters?.sort_direction ||
                "desc",
            ).toLowerCase() ===
            "asc"
              ? "asc"
              : "desc",
        };

        console.log(
          "WATER DCB EXCEL API:",
          params,
        );

        const response =
          await api.get(
            "/api/reports/water-dcb/export",
            {
              params,
              responseType: "blob",
            },
          );

        return response.data;
      } catch (error) {
        const message =
          await getBlobErrorMessage(
            error,
            "Failed to export Water DCB Excel",
          );

        return rejectWithValue(
          message,
        );
      }
    },
  );

// ============================================================
// WATER DCB PDF EXPORT
//
// GET /api/reports/water-dcb/export?format=pdf
// ============================================================

export const exportWaterDcbPdfThunk =
  createAsyncThunk(
    "waterDcb/exportPdf",

    async (
      filters = {},
      { rejectWithValue },
    ) => {
      try {
        const params = {
          format: "pdf",

          fy_year:
            filters?.fy_year || "",

          from_date:
            filters?.from_date || "",

          to_date:
            filters?.to_date || "",

          ward_id:
            filters?.ward_id || "",

          agent_id:
            filters?.agent_id || "",

          ...(filters?.home_id
            ? {
                home_id:
                  filters.home_id,
              }
            : {}),

          sort_field:
            filters?.sort_field ||
            "home_id",

          sort_direction:
            String(
              filters?.sort_direction ||
                "desc",
            ).toLowerCase() ===
            "asc"
              ? "asc"
              : "desc",
        };

        console.log(
          "WATER DCB PDF API:",
          params,
        );

        const response =
          await api.get(
            "/api/reports/water-dcb/export",
            {
              params,
              responseType: "blob",
            },
          );

        return response.data;
      } catch (error) {
        const message =
          await getBlobErrorMessage(
            error,
            "Failed to export Water DCB PDF",
          );

        return rejectWithValue(
          message,
        );
      }
    },
  );

// ============================================================
// SLICE
// ============================================================

const slice = createSlice({
  name: "waterDcb",

  initialState: {
    rows: [],
    meta: {},

    loading: false,
    error: null,

    excelLoading: false,
    excelError: null,

    pdfLoading: false,
    pdfError: null,
  },

  reducers: {
    clearWaterDcb: (state) => {
      state.rows = [];
      state.meta = {};

      state.loading = false;
      state.error = null;

      state.excelLoading = false;
      state.excelError = null;

      state.pdfLoading = false;
      state.pdfError = null;
    },
  },

  extraReducers: (builder) => {
    // ========================================================
    // TABLE
    // ========================================================

    builder.addCase(
      fetchWaterDcbThunk.pending,
      (state) => {
        state.loading = true;
        state.error = null;
      },
    );

    builder.addCase(
      fetchWaterDcbThunk.fulfilled,
      (state, action) => {
        state.loading = false;

        state.rows =
          action.payload?.data || [];

        state.meta =
          action.payload?.meta || {};

        state.error = null;
      },
    );

    builder.addCase(
      fetchWaterDcbThunk.rejected,
      (state, action) => {
        state.loading = false;

        state.error =
          action.payload ||
          action.error?.message ||
          "Failed to load Water DCB report";
      },
    );

    // ========================================================
    // EXCEL
    // ========================================================

    builder.addCase(
      exportWaterDcbExcelThunk.pending,
      (state) => {
        state.excelLoading = true;
        state.excelError = null;
      },
    );

    builder.addCase(
      exportWaterDcbExcelThunk.fulfilled,
      (state) => {
        state.excelLoading = false;
        state.excelError = null;
      },
    );

    builder.addCase(
      exportWaterDcbExcelThunk.rejected,
      (state, action) => {
        state.excelLoading = false;

        state.excelError =
          action.payload ||
          action.error?.message ||
          "Failed to export Water DCB Excel";
      },
    );

    // ========================================================
    // PDF
    // ========================================================

    builder.addCase(
      exportWaterDcbPdfThunk.pending,
      (state) => {
        state.pdfLoading = true;
        state.pdfError = null;
      },
    );

    builder.addCase(
      exportWaterDcbPdfThunk.fulfilled,
      (state) => {
        state.pdfLoading = false;
        state.pdfError = null;
      },
    );

    builder.addCase(
      exportWaterDcbPdfThunk.rejected,
      (state, action) => {
        state.pdfLoading = false;

        state.pdfError =
          action.payload ||
          action.error?.message ||
          "Failed to export Water DCB PDF";
      },
    );
  },
});

export const {
  clearWaterDcb,
} = slice.actions;

export default slice.reducer;